import { useOptionalRuntimeConfig } from '@borradh-workspace/runtime-config/client';
import { useFeatureFlagEnabled, usePostHog } from 'posthog-js/react';
import { useEffect, useState } from 'react';

/**
 * How long {@link useFlagStatus} waits for PostHog to answer before it settles
 * on "off". Flags normally arrive well inside this (and later visits read the
 * persisted values at once); the cap exists for clients whose `/r3y` requests
 * are blocked, which would otherwise never settle.
 */
export const FLAG_LOAD_TIMEOUT_MS = 3_000;

/**
 * `useFlag` (components/posthog-provider) plus whether PostHog has answered
 * yet. Use it where acting on the provisional `false` would be wrong — e.g. a
 * route that redirects when a flag is off must not redirect a user whose flag
 * is about to come back on.
 *
 * `loading` is false once flags have loaded (or failed to), when PostHog is
 * disabled (no `posthogKey`), or after {@link FLAG_LOAD_TIMEOUT_MS}.
 *
 * Lives outside posthog-provider on purpose: that module imports the router,
 * and feature components reading a flag must not drag the route tree in.
 */
export function useFlagStatus(flagKey: string): {
  enabled: boolean;
  loading: boolean;
} {
  const posthogKey = useOptionalRuntimeConfig()?.posthogKey;
  const client = usePostHog();
  const value = useFeatureFlagEnabled(flagKey);
  const [settled, setSettled] = useState(
    () => client?.featureFlags?.hasLoadedFlags === true
  );

  useEffect(() => {
    if (!posthogKey || settled) return;
    // Fires on load AND on a failed load (`errorsLoading`), so a broken flags
    // request settles too rather than waiting for the timeout.
    const unsubscribe = client?.onFeatureFlags?.(() => setSettled(true));
    const timer = setTimeout(() => setSettled(true), FLAG_LOAD_TIMEOUT_MS);
    return () => {
      unsubscribe?.();
      clearTimeout(timer);
    };
  }, [client, posthogKey, settled]);

  return {
    enabled: value === true,
    loading: Boolean(posthogKey) && value === undefined && !settled,
  };
}
